import { InformationCircleIcon } from "@heroicons/react/24/outline"
import { ExportToCsv } from "export-to-csv"
import React, { useState, useEffect } from "react"
import { useQuery } from "urql"

import { CalendarFlyoutProvider } from "../../../contexts/CalendarFlyoutContext"
import { ManualBookingProvider, useManualBooking } from "../../../contexts/ManualBookingContext"
import { PracticeProvider } from "../../../contexts/PracticeContext"
import { BookableEventsProvider } from "../../../pages/BookableEvents/BookableEventsContext"
import DefaultProvider from "../../../providers/DefaultProvider"
import { fetchAppointment } from "../../../utils/fetchAppointment"
import { formatShortDate, formatPrice } from "../../../utils/utils"
import Badge from "../../shared/Badge"
import { Button } from "../../shared/Buttons"
import HealMeTable from "../../shared/HealMeTable"
import HealMeTooltip from "../../shared/HealMeTooltip"
import { Select } from "../../shared/Inputs"
import { TabNavLink } from "../../shared/Links"
import Typography from "../../shared/Typography"
import AppointmentFlyout from "../calendar/AppointmentFlyout"

import CardChargesTable from "./CardChargesTable"
import PaymentSettingsFlyout from "./PaymentSettingsFlyout"

const buildAppointmentsQuery = (includePaymentAttention) => `
  query PaymentDirectory($limit: Int, $offset: Int, $dateFilter: String, $stateFilter: String, $searchTerm: String, $clientId: ID) {
    currentPractice {
      isStripeApproved
      hasPaymentProvider
      requireCcEnabled
      offlinePaymentMethod
      defaultCurrency
      pastAppointments(limit: $limit, offset: $offset, dateFilter: $dateFilter, stateFilter: $stateFilter, searchTerm: $searchTerm, clientId: $clientId) {
        id
        startsAt
        endsAt
        amountCents
        amountCurrency
        tipAmountCents
        fullTotal
        portalPaymentAmountCents
        timeZone
        state
        instantActionToken
        clientName
        paymentStatus
        ${includePaymentAttention ? "paymentAttentionCategory" : ""}
        ${includePaymentAttention ? "paymentAttentionSummary" : ""}
        paymentProcessingAuditSummary
        markPaid
        paidBy
        paymentMethod
        createdAt
        service { id name timeLength }
        appointmentServices {
          amountCents
          service {
            id
            serviceId
            name
            amountCents
            timeLength
            package
          }
        }
        location { id name }
        user { id firstName lastName }
        client { id firstName lastName }
        package { id numberOfSessions createdAt }
        discountCodeRedemptions { code amountCents }
      }
      pastAppointmentsCount(dateFilter: $dateFilter, stateFilter: $stateFilter, searchTerm: $searchTerm, clientId: $clientId)
      totalCollected(clientId: $clientId)
      totalOutstanding(clientId: $clientId)
      manualCharges(clientId: $clientId, dateFilter: $dateFilter, stateFilter: $stateFilter, searchTerm: $searchTerm) {
        id
        capturedAt
        createdAt
        amountCents
        amountCurrency
        note
        state
        paymentMethod
        lastError
        client { firstName lastName }
      }
    }
  }
`

const APPOINTMENTS_QUERY = buildAppointmentsQuery(false)
const APPOINTMENTS_WITH_ATTENTION_QUERY = buildAppointmentsQuery(true)

const dateRangeOptions = [
  { value: "alltime", label: "All time" },
  { value: "thisweek", label: "This week" },
  { value: "lastweek", label: "Last week" },
  { value: "thismonth", label: "This month" },
  { value: "lastmonth", label: "Last month" },
  { value: "thisyear", label: "This year" },
  { value: "lastyear", label: "Last year" }
]

const stateOptions = [
  { value: "allpayments", label: "All payments" },
  { value: "paid", label: "Paid" },
  { value: "upcoming", label: "Upcoming" },
  { value: "unpaid", label: "Unpaid" },
  { value: "free", label: "Free" }
]

const limitOptions = [
  { value: 20, label: "20" },
  { value: 50, label: "50" },
  { value: null, label: "All" }
]

const paymentTabs = {
  all: "all",
  cardCharges: "card_charges",
  needsAttention: "needs_attention"
}

const CARD_CHARGES_TOOLTIP_ID = "card-charges-tab-tooltip"
const CARD_CHARGES_TOOLTIP =
  "Custom amounts charged from a client profile with Charge card. These are not appointment or package payments. A visit you also mark as paid can appear under All payments too."

const consolidatePackages = (appointments) => {
  const seenPackages = new Set()
  const filteredAppointments = []
  for (const item of appointments) {
    const packageId = item.package?.id

    if (packageId) {
      // This is a hack so the "createdAt" accessor for the "Date" header in the table can work both packages and appointments.
      // For packages the date should be the date the package was purchased, not the date of the first appointment.
      item.startsAt = item.package.createdAt
    }
    if (!packageId || !seenPackages.has(packageId)) {
      filteredAppointments.push(item)
      if (packageId) seenPackages.add(packageId)
    }
  }
  return filteredAppointments
}

const TableHeader = ({
  dateFilter,
  stateFilter,
  limit,
  handleFilter,
  totalCount,
  showPagination,
  showSearch,
  showStateFilter
}) => (
  <div className={`flex w-full justify-start gap-2 lg:flex-col sm:ml-0 sm:mt-4 ${showSearch ? "ml-4" : ""}`}>
    <div className="flex flex-row gap-2 sm:justify-between">
      <Select
        options={dateRangeOptions}
        value={dateFilter}
        onChange={(e) => handleFilter(e.target.value, stateFilter, limit)}
        className={showStateFilter ? "sm:w-1/2" : "sm:w-full"}
      />
      {showStateFilter && (
        <Select
          options={stateOptions}
          value={stateFilter}
          onChange={(e) => handleFilter(dateFilter, e.target.value, limit)}
          className="sm:w-1/2"
        />
      )}
    </div>
    {showPagination && (
      <div className="ml-auto flex flex-row items-center gap-2">
        <span>Showing</span>
        <Select
          options={limitOptions}
          value={limit}
          onChange={(e) => handleFilter(dateFilter, stateFilter, e.target.value)}
        />
        <span>of {totalCount}</span>
      </div>
    )}
  </div>
)

const PaymentsTable = ({ communicationSettings, clientId, isImpersonating }) => {
  const { setAppointment } = useManualBooking()

  const [appointmentFlyoutVisible, setAppointmentFlyoutVisible] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [filteredPayments, setFilteredPayments] = useState([])
  const [dateFilter, setDateFilter] = useState(dateRangeOptions[0].value)
  const [stateFilter, setStateFilter] = useState(stateOptions[0].value)
  const [activePaymentTab, setActivePaymentTab] = useState(paymentTabs.all)
  const [limit, setLimit] = useState(limitOptions[0].value)
  const [page, setPage] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const paymentAttentionEnabled = !clientId && isImpersonating
  const cardChargesTabActive = activePaymentTab === paymentTabs.cardCharges
  const attentionTabActive = paymentAttentionEnabled && activePaymentTab === paymentTabs.needsAttention
  const effectiveStateFilter = cardChargesTabActive
    ? "allpayments"
    : attentionTabActive
    ? "needs_attention"
    : stateFilter

  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: attentionTabActive ? APPOINTMENTS_WITH_ATTENTION_QUERY : APPOINTMENTS_QUERY,
    variables: {
      limit: parseInt(limit) || undefined,
      offset: limit ? page * parseInt(limit) : undefined,
      dateFilter,
      stateFilter: effectiveStateFilter,
      searchTerm,
      clientId
    },
    requestPolicy: "cache-and-network"
  })

  if (error) console.error(error) // eslint-disable-line no-console

  useEffect(() => {
    if (data && data.currentPractice) {
      setFilteredPayments(consolidatePackages(data.currentPractice.pastAppointments))
    }
  }, [data])

  const handleFilter = (dateSelection, stateSelection, limitSelection) => {
    setDateFilter(dateSelection || dateFilter)
    setStateFilter(stateSelection || stateFilter)
    setLimit(limitSelection || limit)
    setPage(0)
    reexecuteQuery({ requestPolicy: "cache-and-network" })
  }

  const handleTabClick = (tab) => {
    setActivePaymentTab(tab)
    setPage(0)
  }

  const columns = [
    {
      accessor: "startsAt",
      Header: "Date",
      Cell: (props) => formatShortDate(props.cell.value)
    },
    {
      accessor: "clientName",
      Header: "Client"
    },
    {
      accessor: "service.name",
      Header: "Service"
    },
    {
      accessor: "portalPaymentAmountCents",
      Header: "Amount",
      Cell: (props) => {
        const paymentStatus = props.cell.row.original.paymentStatus
        let textColorClass = ""

        if (paymentStatus === "Unpaid") {
          textColorClass = "text-red"
        } else if (paymentStatus === "Paid") {
          textColorClass = "text-teal"
        } else {
          textColorClass = "text-gray-dark"
        }

        const amount = props.cell.value != null ? props.cell.value : props.cell.row.original.fullTotal

        return (
          <span className={`sm:${textColorClass}`}>
            {formatPrice(amount || 0, props.cell.row.original.amountCurrency)}
          </span>
        )
      }
    },
    {
      accessor: "paymentStatus",
      Header: "Status",
      className: "status",
      Cell: (props) => {
        const status = props.cell.value

        let badgeType
        switch (status) {
          case "Unpaid":
            badgeType = "error"
            break
          case "Free":
            badgeType = "info"
            break
          case "Paid":
            badgeType = "success"
            break
          case "Upcoming":
            badgeType = "warning"
            break
          default:
            badgeType = "success"
        }

        let paidBy
        if (badgeType === "success") {
          if (props.cell.row.original.markPaid) {
            if (!props.cell.row.original.paidBy) {
              paidBy = "offline"
            } else {
              paidBy = props.cell.row.original.paidBy
            }
          } else {
            paidBy = props.cell.row.original.paymentMethod
          }
        }

        return (
          <>
            <Badge type={badgeType}>{props.cell.value}</Badge>
            <span className="ml-2 text-[12px] font-extrabold uppercase tracking-[0.045rem]">{paidBy}</span>
          </>
        )
      }
    },
    {
      accessor: attentionTabActive ? "paymentAttentionSummary" : "paymentProcessingAuditSummary",
      Header: attentionTabActive ? "Issue" : "Payment note",
      className: "max-w-[14rem]",
      Cell: (props) => {
        const note = props.cell.value
        if (!note) return <span className="text-gray-dark-light">—</span>
        const short = note.length > 72 ? `${note.slice(0, 72)}…` : note
        return (
          <span className="text-xs leading-snug text-gray-dark" title={note}>
            {short}
          </span>
        )
      }
    },
    {
      accessor: "instantActionToken",
      Header: "Action",
      className: "row-action",
      Cell: (props) => (
        <>
          {(props.cell.row.original.amountCents > 0 || props.cell.row.original.paymentStatus !== "Free") && (
            <a className="font-bold text-teal" onClick={() => showAppointment(props.cell.value)}>
              Manage
            </a>
          )}
        </>
      )
    }
  ]

  if (clientId) {
    const index = columns.findIndex((obj) => obj.accessor === "clientName")
    if (index !== -1) columns.splice(index, 1)
  }

  const showAppointment = async (instantActionToken) => {
    const selectedAppointment = await fetchAppointment(instantActionToken)
    setAppointmentFlyoutVisible(true)
    setAppointment(selectedAppointment)
  }

  const options = {
    fieldSeparator: ",",
    quoteStrings: '"',
    decimalSeparator: ".",
    showLabels: true,
    useTextFile: false,
    useBom: true,
    filename: attentionTabActive ? "Heal.me Payments - Needs Attention" : "Heal.me Payments",
    headers: [
      "Appointment Date",
      "Client Name",
      "Service",
      "Location",
      "Amount",
      "Status",
      "Paid By",
      attentionTabActive ? "Issue" : "Payment note"
    ]
  }

  const exportToCSV = () => {
    const csvExporter = new ExportToCsv(options)
    const exportable = filteredPayments.map((appt) => ({
      "Appointment Date": formatShortDate(appt.startsAt),
      "Client Name": appt.clientName,
      Service: appt.service.name,
      Location: appt.location.name ? appt.location.name : "",
      Amount: formatPrice(appt.portalPaymentAmountCents ?? appt.fullTotal),
      Status: appt.paymentStatus,
      "Paid By": appt.markPaid ? (appt.paidBy ? appt.paidBy : "offline") : appt.paymentMethod,
      [attentionTabActive ? "Issue" : "Payment note"]: attentionTabActive
        ? appt.paymentAttentionSummary || ""
        : appt.paymentProcessingAuditSummary || ""
    }))
    csvExporter.generateCsv(exportable)
  }

  const genericCallback = () => {
    setAppointmentFlyoutVisible(false)
    setAppointment(null)
    reexecuteQuery()
  }

  if (fetching) {
    return (
      <div className="flex h-screen items-center justify-center">
        <img src="/loading-spinner.png" width="120px" />
      </div>
    )
  }

  if (!data?.currentPractice) {
    return null
  }

  const { currentPractice } = data
  const pastCount = currentPractice.pastAppointmentsCount ?? 0
  const showPaymentsWorkspace =
    (filteredPayments && filteredPayments.length > 0) ||
    pastCount > 0 ||
    currentPractice.hasPaymentProvider ||
    currentPractice.isStripeApproved

  return (
    <>
      {!fetching && (
        <>
          {filteredPayments && showPaymentsWorkspace ? (
            <>
              <div className="mb-8 flex items-end justify-between sm:mb-4">
                <div className="flex flex-col">
                  <Typography as="h1" variant="h3">
                    Payments
                  </Typography>
                  <Typography variant="subtitle" className="sm:hidden">
                    {`${formatPrice(
                      currentPractice.totalCollected * 100,
                      currentPractice.defaultCurrency
                    )} collected \u2022 ${formatPrice(
                      currentPractice.totalOutstanding * 100,
                      currentPractice.defaultCurrency
                    )} outstanding`}
                  </Typography>
                </div>
                <div className="block">
                  {!cardChargesTabActive && (
                    <Button type="tertiary" size="small" onClick={exportToCSV}>
                      Export
                    </Button>
                  )}
                  {currentPractice.requireCcEnabled && (
                    <Button type="tertiary" size="small" className="ml-4" onClick={() => setShowSettings(true)}>
                      Settings
                    </Button>
                  )}
                </div>
              </div>
              <div className="mb-8 mt-4 flex gap-8 border-b border-gray-light">
                <TabNavLink
                  active={activePaymentTab === paymentTabs.all}
                  onClick={() => handleTabClick(paymentTabs.all)}>
                  All payments
                </TabNavLink>
                <TabNavLink active={cardChargesTabActive} onClick={() => handleTabClick(paymentTabs.cardCharges)}>
                  <span className="inline-flex items-center gap-1">
                    Card charges
                    <InformationCircleIcon
                      className="size-4 shrink-0 cursor-help text-gray-dark"
                      data-tooltip-id={CARD_CHARGES_TOOLTIP_ID}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </span>
                </TabNavLink>
                {paymentAttentionEnabled && (
                  <TabNavLink active={attentionTabActive} onClick={() => handleTabClick(paymentTabs.needsAttention)}>
                    Needs attention
                  </TabNavLink>
                )}
              </div>
              <HealMeTooltip id={CARD_CHARGES_TOOLTIP_ID} place="bottom" content={CARD_CHARGES_TOOLTIP} />
              <TableHeader
                dateFilter={dateFilter}
                stateFilter={stateFilter}
                limit={limit}
                handleFilter={handleFilter}
                totalCount={currentPractice.pastAppointmentsCount}
                showPagination={!cardChargesTabActive && currentPractice.pastAppointmentsCount > limit}
                showSearch={!clientId}
                showStateFilter={!cardChargesTabActive && !attentionTabActive}
              />
              {cardChargesTabActive ? (
                <CardChargesTable
                  charges={currentPractice.manualCharges ?? []}
                  hideClientColumn={!!clientId}
                  showSearch={!clientId}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
              ) : (
                <>
                  <HealMeTable
                    data={filteredPayments}
                    columns={columns}
                    searchType={attentionTabActive ? "payments needing attention" : "payments"}
                    hasPagination={false}
                    header={() => null}
                    rowClick={(rowData) => showAppointment(rowData.instantActionToken)}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    searchPlaceholder="Search by name or email"
                    showSearch={!clientId}
                  />
                  {currentPractice.pastAppointmentsCount > limit && (
                    <div className="mt-2 flex justify-center gap-4">
                      <Button disabled={page === 0} className="w-36" onClick={() => setPage(page - 1)}>
                        Previous
                      </Button>
                      <Button
                        disabled={
                          currentPractice.pastAppointments.length < limit ||
                          filteredPayments.length === 0 ||
                          limit === "All"
                        }
                        className="w-36"
                        onClick={() => setPage(page + 1)}>
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="flex h-screen flex-col items-center justify-center rounded-xl bg-gray-ultralight">
              <Typography as="h1" variant="h3">
                Start accepting payments today
              </Typography>
              <Typography variant="subtitle" className="mb-3">
                Accept online payments and get paid on time, every time.
              </Typography>
              <Button type="primary" size="large" onClick={() => (window.location.href = "/portal/settings/banking")}>
                Power-up my practice
              </Button>
            </div>
          )}
        </>
      )}
      <AppointmentFlyout
        visible={appointmentFlyoutVisible}
        closeFlyout={genericCallback}
        onAppointmentChanged={genericCallback}
        onAppointmentCancelled={genericCallback}
        onRescheduleSuccess={genericCallback}
      />
      <PaymentSettingsFlyout
        hasPaymentProvider={currentPractice.hasPaymentProvider}
        hasOffline={currentPractice.offlinePaymentMethod}
        communicationSettings={communicationSettings}
        closeFlyout={() => {
          setShowSettings(false)
        }}
        visible={showSettings}
      />
    </>
  )
}

export default function PaymentDirectory(props) {
  return (
    <DefaultProvider>
      <PracticeProvider>
        <ManualBookingProvider>
          <CalendarFlyoutProvider>
            <BookableEventsProvider>
              <PaymentsTable {...props} />
            </BookableEventsProvider>
          </CalendarFlyoutProvider>
        </ManualBookingProvider>
      </PracticeProvider>
    </DefaultProvider>
  )
}
